import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ImagePlus, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ToolField } from "@/components/tools/ToolField";
import { EmailGateDialog } from "@/components/tools/EmailGateDialog";
import { api, formatApiError, getClientId, getLead, API } from "@/lib/api";

const initial = (tool) => Object.fromEntries(tool.fields.map((f) => [f.name, f.type === "select" ? f.options[0] : ""]));

export const ImageToolPanel = ({ tool }) => {
  const [fields, setFields] = useState(() => initial(tool));
  const [image, setImage] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const loadRemaining = (lead) => lead && api.get("/tools/image/remaining", { params: { email: lead.email } })
    .then(({ data }) => setRemaining(data.remaining)).catch(() => {});

  useEffect(() => { loadRemaining(getLead()); }, []);

  const run = async (lead = getLead()) => {
    if (!lead) { setGateOpen(true); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/tools/image", { ...fields, email: lead.email, client_id: getClientId() });
      setImage({ src: `${API.replace(/\/api$/, "")}${data.url}`, ext: data.ext });
      setRemaining(data.remaining);
    } catch (e) {
      if (e.response?.status === 403) setGateOpen(true);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onUnlocked = (lead) => { loadRemaining(lead); run(lead); };
  const submit = (e) => { e.preventDefault(); run(); };
  const vertical = fields.format.startsWith("Vertical");

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-[#E8E1D7] bg-[#FAF7F2] p-6 sm:p-8 lg:col-span-5">
        <p className="text-sm text-[#6E655F]">{tool.desc}</p>
        {tool.fields.map((f) => (
          <ToolField key={f.name} field={f} value={fields[f.name]} onChange={(v) => setFields((s) => ({ ...s, [f.name]: v }))} />
        ))}
        <Button type="submit" disabled={loading || remaining === 0} data-testid="image-tool-generate-button" className="h-12 w-full rounded-full bg-[#E05A47] text-base hover:bg-[#c94a38] active:scale-[0.98]">
          <ImagePlus className="mr-2 h-4 w-4" /> {loading ? "Creando tu imagen…" : remaining === 0 ? "Vuelve mañana" : "Crear imagen con IA"}
        </Button>
        {remaining !== null && (
          <p data-testid="image-tool-remaining" className="text-center text-sm text-[#6E655F]">
            {remaining > 0 ? `Te quedan ${remaining} imágenes gratis hoy` : "Ya usaste tus imágenes gratis de hoy"}
          </p>
        )}
      </form>
      <ImageResult image={image} loading={loading} vertical={vertical} />
      <EmailGateDialog open={gateOpen} onOpenChange={setGateOpen} tool={tool.id} onUnlocked={onUnlocked} />
    </div>
  );
};

const ImageResult = ({ image, loading, vertical }) => (
  <div className="flex min-h-[380px] flex-col rounded-2xl border border-[#E8E1D7] bg-white p-6 sm:p-8 lg:col-span-7">
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#2A9D8F]">
        <Sparkles className="h-3.5 w-3.5" /> Tu imagen
      </span>
      {image && !loading && (
        <Button asChild variant="outline" size="sm" className="rounded-full">
          <a href={image.src} download={`latina-digital-ia.${image.ext}`} data-testid="image-tool-download-button"><Download className="mr-1.5 h-3.5 w-3.5" /> Descargar</a>
        </Button>
      )}
    </div>
    <div className="mt-4 flex flex-1 items-center justify-center">
      {loading ? (
        <div data-testid="image-tool-loading" className={`flex w-full max-w-sm flex-col items-center justify-center gap-3 rounded-xl bg-[#FAF7F2] text-sm text-[#6E655F] ${vertical ? "aspect-[9/16] max-w-[260px]" : "aspect-square"}`}>
          <Loader2 className="h-8 w-8 animate-spin text-[#E05A47]" /> La IA está creando tu imagen (unos 20 segundos)…
        </div>
      ) : image ? (
        <img data-testid="image-tool-result" src={image.src} alt="Imagen creada con IA" className="max-h-[520px] w-auto rounded-xl object-contain shadow-sm" />
      ) : (
        <p className="text-center text-[#6E655F]">Cuéntanos qué quieres mostrar y pulsa “Crear imagen con IA”. Necesitas dejar tu email (gratis).</p>
      )}
    </div>
  </div>
);
